import { Loader2, LogIn, MoreHorizontal, Store, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { authClient } from "@/lib/auth-client";
import { isConsumerSession, isRestaurantSession } from "@/lib/auth-roles";

export function PublicNavbar() {
  const { t } = useTranslation();
  const { data: session, isPending } = authClient.useSession();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (isPending) {
    return (
      <div
        aria-label={t("storefront.navbar.loading", "Cargando")}
        className="flex h-9 w-9 items-center justify-end"
      >
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isConsumerSession(session)) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to="/account">{t("storefront.navbar.myAccount", "Mi cuenta")}</Link>
      </Button>
    );
  }

  if (isRestaurantSession(session)) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to="/dashboard">
          <Store className="mr-1 h-4 w-4" />
          {t("storefront.navbar.goToDashboard", "Ir al dashboard")}
        </Link>
      </Button>
    );
  }

  const queryString = searchParams.toString();
  const next = queryString ? `${location.pathname}?${queryString}` : location.pathname;
  const consumerSignInPath = `/auth/consumer/sign-in?next=${encodeURIComponent(next)}`;
  const consumerSignUpPath = `/auth/consumer/sign-up?next=${encodeURIComponent(next)}`;
  const restaurantSignInPath = `/auth/restaurant/sign-in?next=${encodeURIComponent(next)}`;
  const moreOptionsLabel = t("storefront.navbar.moreOptions", "Más opciones de cuenta");

  return (
    <nav
      aria-label={t("storefront.navbar.label", "Acceso de cuenta")}
      className="flex items-center gap-2"
    >
      <div className="flex items-center gap-2 sm:hidden">
        <Button asChild size="sm" variant="default">
          <Link to={consumerSignUpPath}>
            <UserPlus className="mr-1 h-4 w-4" />
            {t("storefront.navbar.signUp", "Crear cuenta")}
          </Link>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button aria-label={moreOptionsLabel} size="icon" variant="outline">
              <MoreHorizontal aria-hidden className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-56">
            <DropdownMenuItem asChild>
              <Link className="flex w-full items-center gap-2" to={consumerSignInPath}>
                <LogIn aria-hidden className="h-4 w-4" />
                {t("storefront.navbar.signIn", "Iniciar sesión")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link className="flex w-full items-center gap-2" to={restaurantSignInPath}>
                <Store aria-hidden className="h-4 w-4" />
                {t("storefront.navbar.businessSignIn", "Soy comercio")}
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="hidden items-center gap-2 sm:flex">
        <Button asChild size="sm" variant="ghost">
          <Link to={consumerSignInPath}>
            <LogIn className="mr-1 h-4 w-4" />
            {t("storefront.navbar.signIn", "Iniciar sesión")}
          </Link>
        </Button>
        <Button asChild size="sm" variant="default">
          <Link to={consumerSignUpPath}>
            <UserPlus className="mr-1 h-4 w-4" />
            {t("storefront.navbar.signUp", "Crear cuenta")}
          </Link>
        </Button>
        <Button asChild className="text-muted-foreground" size="sm" variant="link">
          <Link to={restaurantSignInPath}>
            {t("storefront.navbar.businessSignIn", "Soy comercio")}
          </Link>
        </Button>
      </div>
    </nav>
  );
}
